/**
 * JSON-LD structured-data definitions for Vargas Tire Auto Repair.
 *
 * Notes:
 * - No fake reviews or ratings.
 * - No physical address (mobile / service-area business).
 * - Service area = Jacksonville, Florida.
 * - All info kept consistent with the rest of the site.
 */

const SITE_URL = 'https://vargastireautorepair.com';
const PHONE_E164 = '+1-305-772-4150';
const PHONE_DISPLAY = '(305) 772-4150';
const SERVICE_AREA = {
  '@type': 'City',
  name: 'Jacksonville',
  containedInPlace: {
    '@type': 'AdministrativeArea',
    name: 'Florida, United States',
  },
};

const SERVICE_NAMES = [
  'Mobile Oil Change',
  'Roadside Assistance',
  'Flat Tire Help',
  'Tire Service',
  'Battery Jump',
  'Lockout Service',
  'Fuel Delivery',
  'Mobile Diagnostics',
  'Brake Service',
  'Scheduled Car Care',
];

export const homepageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'AutoRepair',
  '@id': `${SITE_URL}/#business`,
  name: 'Vargas Tire Auto Repair',
  alternateName: 'Vargas Car Care',
  url: `${SITE_URL}/`,
  telephone: PHONE_E164,
  description:
    'Mobile mechanic, tire service and 24/7 roadside assistance serving Jacksonville, Florida. Mobile oil change, brake service, battery jump, lockout, fuel delivery, mobile diagnostics and scheduled car care.',
  knowsAbout: SERVICE_NAMES,
  areaServed: SERVICE_AREA,
  serviceArea: SERVICE_AREA,
  priceRange: '$$',
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
      ],
      opens: '08:00',
      closes: '18:00',
      description: 'Regular service hours (8 AM – 6 PM).',
    },
  ],
  specialOpeningHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      validFrom: '2026-01-01',
      validThrough: '2099-12-31',
      opens: '00:00',
      closes: '23:59',
      description:
        '24/7 emergency roadside assistance. After-hours service (outside 8 AM – 6 PM) includes a $60 call-out fee.',
    },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Vargas Car Care Services',
    itemListElement: SERVICE_NAMES.map((name) => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name,
        areaServed: SERVICE_AREA,
        provider: {
          '@type': 'AutoRepair',
          name: 'Vargas Tire Auto Repair',
          telephone: PHONE_E164,
        },
      },
    })),
  },
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: PHONE_E164,
      contactType: 'customer service',
      areaServed: 'US-FL',
      availableLanguage: ['English', 'Spanish'],
    },
    {
      '@type': 'ContactPoint',
      telephone: PHONE_E164,
      contactType: 'emergency',
      areaServed: 'US-FL',
      availableLanguage: ['English', 'Spanish'],
      hoursAvailable: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
          'Sunday',
        ],
        opens: '00:00',
        closes: '23:59',
      },
    },
  ],
  disclaimer:
    'Prices are starting prices only. Final price may vary by vehicle, location, parts, fuel, tire size, towing distance, and service condition.',
  potentialAction: {
    '@type': 'ReserveAction',
    target: `${SITE_URL}/roadside`,
    name: 'Request roadside assistance',
  },
  sameAs: [],
};

const BLOG_SERVICES = [
  {
    name: 'Mobile Oil Change Jacksonville',
    description:
      'Mobile oil change service performed at your home, office, or job site in Jacksonville, Florida.',
  },
  {
    name: 'Roadside Assistance Jacksonville',
    description:
      '24/7 roadside assistance in Jacksonville for flat tires, jump starts, lockouts, fuel delivery and tows.',
  },
  {
    name: 'Mobile Tire Service Jacksonville',
    description:
      'Mobile tire installation, replacement and flat-tire repair across Jacksonville, Florida.',
  },
  {
    name: 'Brake Service Jacksonville',
    description:
      'Brake pad and rotor service, brake inspection and brake repair in Jacksonville.',
  },
  {
    name: 'Battery Jump Service Jacksonville',
    description:
      'Mobile battery jump start and battery replacement service in Jacksonville, Florida.',
  },
];

export const blogStructuredData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Blog',
      '@id': `${SITE_URL}/blog#blog`,
      name: 'Vargas Tire Auto Repair Blog',
      headline: 'Vargas Tire Auto Repair Blog',
      url: `${SITE_URL}/blog`,
      inLanguage: 'en-US',
      description:
        'Car care tips, mobile mechanic advice, roadside assistance information, tire service, brake service, battery help, and mobile oil change tips for Jacksonville drivers.',
      about: SERVICE_NAMES,
      keywords: [
        'mobile mechanic Jacksonville',
        'mobile oil change Jacksonville',
        'roadside assistance Jacksonville',
        'tire service Jacksonville',
        'brake service Jacksonville',
        'battery jump',
        'lockout service',
        'car care tips',
      ],
      publisher: {
        '@type': 'AutoRepair',
        '@id': `${SITE_URL}/#business`,
        name: 'Vargas Tire Auto Repair',
        alternateName: 'Vargas Car Care',
        url: `${SITE_URL}/`,
        telephone: PHONE_E164,
        areaServed: SERVICE_AREA,
      },
    },
    ...BLOG_SERVICES.map((s, idx) => ({
      '@type': 'Service',
      '@id': `${SITE_URL}/blog#service-${idx + 1}`,
      name: s.name,
      description: s.description,
      areaServed: SERVICE_AREA,
      provider: {
        '@type': 'AutoRepair',
        '@id': `${SITE_URL}/#business`,
        name: 'Vargas Tire Auto Repair',
        alternateName: 'Vargas Car Care',
        telephone: PHONE_E164,
        url: `${SITE_URL}/`,
      },
      serviceType: 'Mobile auto repair and roadside service',
      url: `${SITE_URL}/blog`,
    })),
  ],
};

export const businessConstants = {
  SITE_URL,
  PHONE_E164,
  PHONE_DISPLAY,
  SERVICE_AREA,
};
